import React, { useState } from 'react';
import {
  Table,
  Button,
  Space,
  Checkbox,
  Row,
  Col,
  message,
  Modal,
  Typography,
} from 'antd';
import {
  GeneralContainer,
  FormSelect,
  CustomTable,
} from '../../../../components';
import { useNavigate } from 'react-router-dom';
import SearchFilters from '../components/SearchFilters';
import AdvancedFiltersView from './AdvancedFiltersView';
import { OnlineInventoryAPI } from '../../api/onlineInventoryAPI';
import { useQueryClient } from '@tanstack/react-query';
import { ExclamationCircleOutlined } from '@ant-design/icons';

const { Text } = Typography;

interface OnlineInventoryViewArchivedProps {
  movements: Array<any>;
  isLoading: boolean;
  formikProps: any;
  onViewCreate: () => void;
  onOpenFilters: () => void;
  onApplyFilters: (filters: any) => void;
  filters: any[];
  isFilterModalVisible: boolean;
  setIsFilterModalVisible: (visible: boolean) => void;
}

const OnlineInventoryViewArchived: React.FC<
  OnlineInventoryViewArchivedProps
> = ({ movements, isLoading, formikProps, onViewCreate, onApplyFilters }) => {
  const { resetForm } = formikProps;

  const [isFilterModalVisible, setIsFilterModalVisible] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<number | null>(null);

  const [filters, setFilters] = useState<any>([]);
  const queryClient = useQueryClient();

  // Abrir modal de filtros avanzados
  const handleOpenFilters = () => {
    setIsFilterModalVisible(true);
  };

  // Cerrar modal de filtros avanzados
  const handleCloseFilters = () => {
    setIsFilterModalVisible(false);
  };

  const showUnarchiveModal = (assetId?: number) => {
    if (assetId) {
      setSelectedAsset(assetId);
    } else if (selectedRowKeys.length > 0) {
      setSelectedAsset(null);
    }
    setIsModalVisible(true);
  };

  const handleUnarchiveSelected = async () => {
    const idsArray =
      selectedAsset !== null ? [selectedAsset] : selectedRowKeys.map(Number);

    if (idsArray.length === 0) {
      message.warning('No hay solicitudes seleccionadas.');
      return;
    }

    try {
      await OnlineInventoryAPI.toggleArchiveAssets(idsArray);
      message.success('Solicitudes desarchivadas exitosamente.');

      setSelectedRowKeys([]);
      setIsModalVisible(false);
      setSelectedAsset(null);
      queryClient.invalidateQueries({ queryKey: ['OnlineInventoryArchived'] });
    } catch (error) {
      message.error('Hubo un problema al desarchivar las solicitudes.');
    }
  };

  // Configurar selección de filas
  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) =>
      setSelectedRowKeys(newSelectedRowKeys),
  };

  const handleArchiveSelected = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('No hay solicitudes seleccionadas.');
      return;
    }

    try {
      await OnlineInventoryAPI.toggleArchiveAssets(selectedRowKeys as number[]);
      message.success('Solicitudes archivadas exitosamente.');
      setSelectedRowKeys([]); // Limpiar la selección después de archivar
    } catch (error) {
      message.error('Hubo un problema al archivar las solicitudes.');
    }
  };

  const columns = [
    {
      title: 'Código inventario',
      dataIndex: 'inventory_code',
      key: 'inventory_code',
      sorter: true,
    },
    {
      title: 'Código/Placa',
      dataIndex: 'plate_code',
      key: 'plate_code',
      sorter: true,
    },
    { title: 'Activo', dataIndex: 'item_name', key: 'item_name', sorter: true },
    {
      title: 'Placa alterna',
      dataIndex: 'plate_code_alternative',
      key: 'plate_code_alternative',
      sorter: true,
    },
    {
      title: 'Responsable',
      dataIndex: 'responsible',
      key: 'responsible',
      sorter: true,
    },
    {
      title: 'Descripción',
      dataIndex: 'description',
      key: 'description',
      sorter: true,
    },
    { title: 'Regional', dataIndex: 'regional', key: 'regional', sorter: true },
    {
      title: 'Seccional',
      dataIndex: 'sectional',
      key: 'sectional',
      sorter: true,
    },
    {
      title: 'Dependencia',
      dataIndex: 'dependency',
      key: 'dependency',
      sorter: true,
    },
    {
      title: 'Estado de validación',
      dataIndex: 'inventory_status',
      key: 'inventory_status',
      sorter: true,
      render: (status: { name: string }) => status?.name || 'N/A',
    },
    {
      title: 'Fecha de última verificación',
      dataIndex: 'last_verification_date',
      key: 'last_verification_date',
      sorter: true,
      render: (date: string | null) => (date ? date : 'No registrada'),
    },
    {
      title: 'Centro de costo',
      dataIndex: 'cost_center',
      key: 'cost_center',
      sorter: true,
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_: any, record: any) => (
        <Button
          type="primary"
          style={{ backgroundColor: 'green', borderColor: 'green' }}
          onClick={() => showUnarchiveModal(record.id)}
        >
          Desarchivar
        </Button>
      ),
    },
  ];

  return (
    <GeneralContainer title="Mi Inventario en Línea">
      <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
        <Col xs={24}>
          <SearchFilters
            formik={formikProps}
            isLoading={isLoading}
            onCancel={resetForm}
            onOpenFilters={handleOpenFilters}
          />
        </Col>
      </Row>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '10px',
        }}
      >
        <Checkbox
          onChange={(e) =>
            setSelectedRowKeys(
              e.target.checked ? movements.map((item) => item.id) : []
            )
          }
          checked={
            selectedRowKeys.length === movements.length && movements.length > 0
          }
        >
          Seleccionar todos
        </Checkbox>
        <Button
          type="primary"
          onClick={() => showUnarchiveModal()}
          disabled={selectedRowKeys.length === 0}
        >
          Desarchivar Solicitudes
        </Button>
      </div>

      <CustomTable
        columns={columns}
        dataSource={movements}
        loading={isLoading}
        rowKey="id"
        pagination={{ pageSize: 5 }}
        scroll={{ x: 'max-content' }}
        rowSelection={rowSelection}
      />

      <Modal
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        centered
      >
        <Row justify="center" style={{ marginBottom: 20 }}>
          <Col>
            <ExclamationCircleOutlined
              style={{ fontSize: 50, color: '#faad14' }}
            />
          </Col>
        </Row>

        <Row justify="center" style={{ textAlign: 'center', marginBottom: 10 }}>
          <Col>
            <Text strong>Desarchivar Elemento</Text>
            <p style={{ marginTop: 10 }}>
              ¿Desea desarchivar el(los) elementos seleccionados?
            </p>
          </Col>
        </Row>

        <Row justify="center">
          <Button
            onClick={() => setIsModalVisible(false)}
            style={{ marginRight: 10 }}
          >
            No
          </Button>
          <Button type="primary" onClick={handleUnarchiveSelected}>
            Sí
          </Button>
        </Row>
      </Modal>

      {isFilterModalVisible && (
        <AdvancedFiltersView
          onClose={handleCloseFilters}
          onApplyFilters={onApplyFilters}
        />
      )}
    </GeneralContainer>
  );
};

export default OnlineInventoryViewArchived;
